import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  /**
   * @type {AbortController | null}
   */
  #currentRequest = null;

  /**
   * Normalize text for comparison (lowercase, trim, remove diacritics)
   * @param {string} value
   * @returns {string}
   */
  #normalizeText(value) {
    return (value || '')
      .toString()
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '');
  }

  connectedCallback() {
    super.connectedCallback();
    
    // Add input event listener for AJAX search
    if (window.searchAjax) {
      this.refs.searchPageInput.addEventListener('input', this.handleSearchInput);
      
      // Add click event listener for reset button
      const resetButton = document.querySelector('.search__reset-button');
      if (resetButton) {
        resetButton.addEventListener('click', this.handleResetClick);
      }
      
      // Check for saved search term on page load (only for non-search pages)
      const currentUrl = new URL(window.location.href);
      const isSearchPage = currentUrl.pathname.includes('/search');
      
      if (!isSearchPage) {
        this.#checkSavedSearchTerm();
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    // Clean up
    if (this.#currentRequest) {
      this.#currentRequest.abort();
    }
    
    if (window.searchAjax) {
      this.refs.searchPageInput.removeEventListener('input', this.handleSearchInput);
      
      // Remove reset button event listener
      const resetButton = document.querySelector('.search__reset-button');
      if (resetButton) {
        resetButton.removeEventListener('click', this.handleResetClick);
      }
    }
  }

  /**
   * Handles the click event on the clear button and submits an empty search.
   * This clears the search input and resubmits the form if the page is not
   * already in an empty state.
   */
  handleClearClick() {
    this.#submitEmptySearch();
  }

  /**
   * Handles the reset button click event
   *
   * @param {MouseEvent} event - The click event.
   */
  handleResetClick = async (event) => {
    event.preventDefault();
    
    // Clear the input
    this.refs.searchPageInput.value = '';
    
    // Check if we're on search page or other pages
    const currentUrl = new URL(window.location.href);
    const isSearchPage = currentUrl.pathname.includes('/search');
    
    if (isSearchPage) {
      // Reset completely on the /search page: remove q and all filter params
      await this.#resetToBaseSearch();
    } else {
      // Show all products for collection/other pages
      this.#showAllProducts();
    }
  };

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();
    
    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  /**
   * Handles the input event for AJAX search functionality
   *
   * @param {InputEvent} event - The input event.
   */
  handleSearchInput = debounce((event) => {
    // Only proceed if AJAX search is enabled
    if (!window.searchAjax) return;
    
    const searchTerm = event.target.value.trim();
    
    // Check if we're on search page or other pages
    const currentUrl = new URL(window.location.href);
    const isSearchPage = currentUrl.pathname.includes('/search');
    
    if (isSearchPage) {
      // Use AJAX for search pages
      if (searchTerm.length === 0) {
        this.#handleEmptySearch();
        return;
      }

      if (searchTerm.length >= 2) {
        this.#performAjaxSearch(searchTerm);
      }
    } else {
      // Use visual filtering for collection/other pages
      if (searchTerm.length === 0) {
        this.#showAllProducts();
        return;
      }

      if (searchTerm.length >= 2) {
        this.#filterProductsVisually(searchTerm);
      }
    }
  }, 300);

  /**
   * Performs the AJAX search request
   *
   * @param {string} searchTerm - The search term
   */
  async #performAjaxSearch(searchTerm) {
    try {
      if (this.#currentRequest) {
        this.#currentRequest.abort();
      }

      this.#currentRequest = new AbortController();
      this.#setLoadingState(true);

      const searchUrl = this.#buildSearchUrl(searchTerm);
      this.#updateHistory(searchUrl);

      const response = await fetch(searchUrl, {
        method: 'GET',
        headers: {
          'Accept': 'text/html,application/xhtml+xml',
          'X-Requested-With': 'XMLHttpRequest'
        },
        signal: this.#currentRequest.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const htmlContent = await response.text();
    
      this.#updateSearchResults(htmlContent);

    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      
      console.error('Error performing AJAX search:', error);
      this.#handleSearchError(error);
    } finally {
      this.#setLoadingState(false);
      this.#currentRequest = null;
    }
  }

  /**
   * Builds the search URL with query parameters (only for search pages)
   *
   * @param {string} searchTerm - The search term
   * @returns {string} The complete search URL
   */
  #buildSearchUrl(searchTerm) {
    const currentUrl = new URL(window.location.href);
    const baseUrl = currentUrl.origin + currentUrl.pathname;
    
    const params = new URLSearchParams();
    params.set('q', searchTerm);
    params.set('options[prefix]', 'last');
    
    return `${baseUrl}?${params.toString()}`;
  }

  /**
   * Updates the browser history without reloading the page (only for search pages)
   *
   * @param {string} url - The URL to push to history
   */
  #updateHistory(url) {
    if (window.history && window.history.pushState) {
      window.history.pushState({ searchAjax: true }, '', url);
    }
  }

  /**
   * Filters products visually by showing/hiding based on search term
   *
   * @param {string} searchTerm - The search term to filter by
   */
  #filterProductsVisually(searchTerm) {
    // Save search term to sessionStorage for collection pages
    const currentUrl = new URL(window.location.href);
    const isSearchPage = currentUrl.pathname.includes('/search');
    
    if (!isSearchPage) {
      sessionStorage.setItem('collectionSearchTerm', searchTerm);
      // Also update the input value to reflect the search term
      this.refs.searchPageInput.value = searchTerm;
    }
    
    // Get all product elements (you'll need to adjust selectors based on your HTML structure)
    const productSelectors = [
      '.product-grid .product-grid__item',
      '.product-grid .grid__item',
      '.collection .product-grid .grid__item',
      '.collection .grid .grid__item',
      '.grid--view-items .grid__item',
      '[data-product-item]'
    ];
    
    let products = [];
    let usedProductSelector = null;
    for (const selector of productSelectors) {
      const found = document.querySelectorAll(selector);
      if (found.length > 0) {
        products = Array.from(found);
        usedProductSelector = selector;
        break;
      }
    }
    
    if (products.length === 0) {
      /* No products found to filter */
      return;
    }
    
    const searchTermLower = this.#normalizeText(searchTerm);
    let visibleCount = 0;
    let sampleLogged = 0;
    
    products.forEach(product => {
      // Get product title from various possible selectors
      const titleSelectors = [
        '.card__heading a',
        '.card__heading',
        '.product-card__title',
        '.product-grid__title a',
        '.product-grid__title',
        '.product-item__title',
        '.product-title',
        '.card__heading a',
        '.card__heading',
        '.product-card__title',
        '.card-information__text a',
        '.card-information__text',
        '[data-product-title]',
        '.full-unstyled-link',
        'a[href*="/products/"]',
        '.text-block h3',
        '.text-block h2',
        '.product-grid--grid .text-block p'
      ];
      
      let productTitle = '';
      let usedTitleSelector = null;
      for (const selector of titleSelectors) {
        const titleElement = product.querySelector(selector);
        if (titleElement) {
          productTitle = titleElement.textContent || titleElement.innerText || '';
          usedTitleSelector = selector;
          break;
        }
      }

      // Fallbacks if no explicit title element is found
      if (!productTitle) {
        // Try aria-label/title attributes on links
        const linkEl = product.querySelector('a[href*="/products/"]');
        if (linkEl) {
          productTitle =
            linkEl.getAttribute('aria-label') ||
            linkEl.getAttribute('title') || '';
          if (productTitle) {
            usedTitleSelector = 'a[href*="/products/"]@attr';
          }
        }
      }
      
      // Also check if we have a global products array from Liquid
      if (!productTitle && window.collectionProducts) {
        const productId = product.dataset.productId || product.getAttribute('data-product-id');
        const productData = window.collectionProducts.find(p => p.id == productId);
        if (productData) {
          productTitle = productData.title || '';
          if (productTitle) {
            usedTitleSelector = 'window.collectionProducts';
          }
        }
      }

      // Last resort: use trimmed textContent of the card (can be noisy)
      if (!productTitle) {
        productTitle = (product.textContent || '').trim().slice(0, 200);
        if (productTitle) {
          usedTitleSelector = 'product.textContent';
        }
      }
      
      // Check if product title matches search term
      const matches = this.#normalizeText(productTitle).includes(searchTermLower);
      
      if (matches) {
        product.style.display = '';
        product.classList.remove('product-hidden');
        product.classList.add('product-visible');
        visibleCount++;
      } else {
        product.style.display = 'none';
        product.classList.add('product-hidden');
        product.classList.remove('product-visible');
      }

      // Log a few samples for debugging
      if (sampleLogged < 3) {
        /* sample debug suppressed */
        sampleLogged++;
      }
    });
    
    // Show a message if no products match
    this.#updateNoResultsMessage(visibleCount === 0, searchTerm);
    
  }

  /**
   * Shows all products (removes visual filtering)
   */
  #showAllProducts() {
    // Clear saved search term for collection pages
    const currentUrl = new URL(window.location.href);
    const isSearchPage = currentUrl.pathname.includes('/search');
    
    if (!isSearchPage) {
      sessionStorage.removeItem('collectionSearchTerm');
    }
    
    const productSelectors = [
      '.product-grid .product-grid__item'
    ];
    
    let products = [];
    let usedProductSelector = null;
    for (const selector of productSelectors) {
      const found = document.querySelectorAll(selector);
      if (found.length > 0) {
        products = Array.from(found);
        usedProductSelector = selector;
        break;
      }
    }
    
    products.forEach(product => {
      product.style.display = '';
      product.classList.remove('product-hidden');
      product.classList.add('product-visible');
    });
    
    // Hide no results message
    this.#updateNoResultsMessage(false);
    
  }

  /**
   * Checks for saved search term on page load and applies it
   */
  #checkSavedSearchTerm() {
    const savedTerm = sessionStorage.getItem('collectionSearchTerm');
    
    if (savedTerm && savedTerm.trim().length >= 2) {
      // Update input field with saved term
      this.refs.searchPageInput.value = savedTerm;
      
      // Apply visual filtering with saved term
      this.#filterProductsVisually(savedTerm);
      
    }
  }

  /**
   * Public method to reapply search filter after HTML updates (e.g., after filter fetch)
   * This should be called from outside when collection HTML is updated
   */
  reapplySearchFilter() {
    const currentUrl = new URL(window.location.href);
    const isSearchPage = currentUrl.pathname.includes('/search');
    
    // Only reapply for non-search pages
    if (!isSearchPage) {
      const savedTerm = sessionStorage.getItem('collectionSearchTerm');
      
      if (savedTerm && savedTerm.trim().length >= 2) {
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          this.#filterProductsVisually(savedTerm);
        }, 100);
      }
    }
  }

  /**
   * Public method to clear search filter
   * Can be called from outside when needed
   */
  clearSearchFilter() {
    const currentUrl = new URL(window.location.href);
    const isSearchPage = currentUrl.pathname.includes('/search');
    
    if (!isSearchPage) {
      sessionStorage.removeItem('collectionSearchTerm');
      this.refs.searchPageInput.value = '';
      this.#showAllProducts();
    }
  }

  /**
   * Updates the search results in the DOM
   *
   * @param {string} htmlContent - The HTML content from the server
   */
  #updateSearchResults(htmlContent) {
    const tempContainer = document.createElement('div');
    tempContainer.style.display = 'none';
    document.body.appendChild(tempContainer);
    
    try {
      if (tempContainer.setHTMLUnsafe) {
        tempContainer.setHTMLUnsafe(htmlContent);
      } else {
        // Fallback for older browsers
        tempContainer.innerHTML = htmlContent;
      }
      
      // Try multiple selectors for results container (collection vs search pages)
      const resultSelectors = [
        'results-list',
        '.results-list', 
        '#results-list', 
        '[data-results-list]',
        '.collection-products',
        '.search-results',
        '.product-grid',
        '.grid--view-items'
      ];
      
      let newResultsList = null;
      let currentResultsList = null;
      
      // Find results in response
      for (const selector of resultSelectors) {
        newResultsList = tempContainer.querySelector(selector);
        if (newResultsList) break;
      }
      
      // Find current results on page
      for (const selector of resultSelectors) {
        currentResultsList = document.querySelector(selector);
        if (currentResultsList) break;
      }
      
      if (newResultsList && currentResultsList) {
        // Update the results content directly
        currentResultsList.innerHTML = newResultsList.innerHTML;
        
        currentResultsList.classList.add('search-results-updated');
        
        setTimeout(() => {
          currentResultsList.classList.remove('search-results-updated');
        }, 300);
      } else {
      }
    } catch (error) {
      console.error('Error updating search results:', error);
      this.#handleSearchError(error);
    } finally {
      // Clean up temporary container
      document.body.removeChild(tempContainer);
    }
  }

  /**
   * Updates or shows/hides no results message
   *
   * @param {boolean} show - Whether to show the no results message
   * @param {string} searchTerm - The search term that produced no results
   */
  #updateNoResultsMessage(show, searchTerm = '') {
    const messageId = 'visual-search-no-results';
    let message = document.getElementById(messageId);
    
    if (show && !message) {
      // Create no results message
      message = document.createElement('div');
      message.id = messageId;
      message.className = 'visual-search-no-results';
      message.innerHTML = `
        <div class="no-results-content">
          <p>No products found matching "${searchTerm}"</p>
          <p>Try adjusting your search or browse all products.</p>
        </div>
      `;
      
      // Find results container and add message
      const resultSelectors = [
        '.collection-products',
        '.product-grid',
        '.grid--view-items',
        '.results-list',
        '.products-grid',
        '#CollectionProductGrid',
        '.collection .product-grid',
        '.collection__products',
        '.collection-grid'
      ];
      
      let container = null;
      for (const selector of resultSelectors) {
        container = document.querySelector(selector);
        if (container) break;
      }
      
      if (container) {
        container.appendChild(message);
      }
    } else if (!show && message) {
      // Remove no results message
      message.remove();
    }
  }

  /**
   * Handles empty search state - keeps previous results visible
   */
  #handleEmptySearch() {
    // Update URL to show empty search
    const currentUrl = new URL(window.location.href);
    const baseUrl = currentUrl.origin + currentUrl.pathname;
    
    this.#updateHistory(baseUrl);
    
    // Don't clear results - keep previous products visible
    // This allows users to see previous results when they clear the search
  }

  /**
   * Resets the search page to its base URL (removes q and all filter params)
   * and refreshes results. Falls back to full reload if AJAX update fails.
   */
  async #resetToBaseSearch() {
    const currentUrl = new URL(window.location.href);
    const baseUrl = currentUrl.origin + currentUrl.pathname; // /search without params

    try {
      // Update history first so the URL is clean
      this.#updateHistory(baseUrl);

      // Try to fetch base page and update results via DOM replacement
      const response = await fetch(baseUrl, {
        method: 'GET',
        headers: {
          'Accept': 'text/html,application/xhtml+xml',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      this.#updateSearchResults(html);
    } catch (err) {
      // As a robust fallback, force navigation to the base URL
      window.location.href = baseUrl;
    }
  }

  /**
   * Sets the loading state visual indicators (only for search pages)
   *
   * @param {boolean} isLoading - Whether the search is loading
   */
  #setLoadingState(isLoading) {
    // Only show loading states for search pages
    const currentUrl = new URL(window.location.href);
    const isSearchPage = currentUrl.pathname.includes('/search');
    
    if (!isSearchPage) return;
    
    // Try multiple selectors for results container
    const resultSelectors = [
      'results-list',
      '.results-list', 
      '#results-list', 
      '[data-results-list]',
      '.search-results',
      '.product-grid',
      '.grid--view-items'
    ];
    
    let resultsList = null;
    for (const selector of resultSelectors) {
      resultsList = document.querySelector(selector);
      if (resultsList) break;
    }
    
    const searchInput = this.refs.searchPageInput;
    
    if (isLoading) {
      resultsList?.classList.add('search-loading');
      searchInput.classList.add('search-input-loading');
    } else {
      resultsList?.classList.remove('search-loading');
      searchInput.classList.remove('search-input-loading');
    }
  }

  /**
   * Handles search errors
   *
   * @param {Error} error - The error that occurred
   */
  #handleSearchError(error) {
    console.error('Search error:', error);
    // Could optionally show a simple notification or maintain previous results
  }

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    // If AJAX is enabled, handle empty search with AJAX (keeps previous results)
    if (window.searchAjax) {
      this.#handleEmptySearch();
    } else {
      searchInput.form?.submit();
    }
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
